import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ContentWrap } from '../components/ContentWrap.js';
import { Button } from '../components/Buttons/Button.js';
import { Heading, PageContent } from '../components/index.js';
import { getMessage } from '../i18n/messages.js';
import { classifyAxiosError } from '@digiwedge/hooks-auth-web';
import { useOptionalIdp } from '../providers/IdpProvider.js';
import { toast } from '../utils/toast.js';
import { IdpRequestError, postIdpJson } from '../utils/idp-http.js';
const MIN_PASSWORD_LENGTH = 8;
function buildLoginHref(returnTo) {
    if (!returnTo) {
        return '/login';
    }
    const params = new URLSearchParams();
    params.set('returnTo', returnTo);
    return `/login?${params.toString()}`;
}
export const ResetPasswordPage = () => {
    const location = useLocation();
    const params = useParams();
    const queryToken = useMemo(() => {
        try {
            const query = new URLSearchParams(location.search);
            return query.get('token') ?? query.get('t');
        }
        catch {
            return null;
        }
    }, [location.search]);
    const returnTo = useMemo(() => {
        try {
            const query = new URLSearchParams(location.search);
            const candidate = query.get('returnTo');
            return candidate && candidate.startsWith('/') ? candidate : null;
        }
        catch {
            return null;
        }
    }, [location.search]);
    const loginHref = useMemo(() => buildLoginHref(returnTo), [returnTo]);
    const token = params.token ?? queryToken ?? '';
    const hasToken = Boolean(token);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [touchedPassword, setTouchedPassword] = useState(false);
    const [touchedConfirm, setTouchedConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const idp = useOptionalIdp();
    const resetPassword = useMemo(() => {
        if (idp?.resetPassword) {
            return idp.resetPassword;
        }
        return (params) => postIdpJson('/api/auth/reset-password', params);
    }, [idp]);
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const passwordInvalid = password.length < MIN_PASSWORD_LENGTH || !hasUppercase || !hasLowercase || !hasNumber;
    const mismatch = confirmPassword.length > 0 && password !== confirmPassword;
    const onSubmit = async (event) => {
        event.preventDefault();
        setTouchedPassword(true);
        setTouchedConfirm(true);
        if (!hasToken) {
            setError(getMessage('en-US', 'resetPasswordInvalidLink'));
            return;
        }
        if (passwordInvalid) {
            setError(getMessage('en-US', 'resetPasswordRequirements'));
            return;
        }
        if (password !== confirmPassword) {
            setError(getMessage('en-US', 'resetPasswordMismatch'));
            return;
        }
        setError(null);
        setLoading(true);
        try {
            await resetPassword({ token, password });
            setSuccess(true);
            toast.success(getMessage('en-US', 'resetPasswordSuccess'));
        }
        catch (err) {
            const fallback = getMessage('en-US', 'genericError');
            if (isAxiosError(err)) {
                const result = classifyAxiosError(err, fallback);
                if (result.code === 'invalid-request' || result.code === 'otp-expired') {
                    const invalidMessage = getMessage('en-US', 'resetPasswordInvalidLink');
                    setError(invalidMessage);
                    toast.error(invalidMessage);
                }
                else {
                    setError(result.message);
                    toast.error(result.message);
                }
            }
            else if (err instanceof IdpRequestError) {
                if (err.status && (err.status === 400 || err.status === 410 || err.status === 422)) {
                    const invalidMessage = getMessage('en-US', 'resetPasswordInvalidLink');
                    setError(invalidMessage);
                    toast.error(invalidMessage);
                }
                else {
                    const msg = err.message || fallback;
                    setError(msg);
                    toast.error(msg);
                }
            }
            else {
                setError(fallback);
                toast.error(fallback);
            }
        }
        finally {
            setLoading(false);
        }
    };
    const forgotHref = useMemo(() => {
        const params = new URLSearchParams();
        if (returnTo) {
            params.set('returnTo', returnTo);
        }
        const query = params.toString();
        return query ? `/forgot-password?${query}` : '/forgot-password';
    }, [returnTo]);
    return (_jsx(PageContent, { children: _jsx(ContentWrap, { className: "flex justify-center py-12 items-center", children: _jsxs("div", { className: "w-full max-w-md bg-white px-6 py-10 rounded-lg shadow flex flex-col gap-6", children: [_jsxs("div", { className: "text-center flex flex-col gap-3", children: [_jsx(Heading, { variant: "h4", children: getMessage('en-US', 'resetPasswordHeading') }), _jsx("p", { className: "text-sm text-gray-600", children: getMessage('en-US', 'resetPasswordDescription') })] }), success ? (_jsxs("div", { className: "flex flex-col gap-3 text-center", children: [_jsx("p", { className: "text-sm text-gray-700", children: getMessage('en-US', 'resetPasswordSuccess') }), _jsx(Link, { to: loginHref, className: "text-primary hover:underline text-sm", children: getMessage('en-US', 'resetPasswordGoToLogin') })] })) : (_jsxs("form", { onSubmit: onSubmit, className: "flex flex-col gap-4", noValidate: true, children: [!hasToken && (_jsx("div", { className: "bg-yellow-50 border border-yellow-200 text-yellow-700 text-sm p-3 rounded", role: "alert", children: getMessage('en-US', 'resetPasswordInvalidLink') })), _jsxs("div", { children: [_jsx("label", { htmlFor: "password", className: "block text-sm font-medium mb-1", children: getMessage('en-US', 'resetPasswordPasswordLabel') }), _jsx("input", { id: "password", type: "password", required: true, value: password, onChange: (e) => setPassword(e.target.value), onBlur: () => setTouchedPassword(true), className: "w-full h-11 px-3 border border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", "aria-invalid": touchedPassword && passwordInvalid }), _jsx("p", { className: "mt-1 text-xs text-gray-600", children: getMessage('en-US', 'resetPasswordRequirements') }), touchedPassword && passwordInvalid && (_jsx("p", { className: "mt-1 text-xs text-red-600", children: getMessage('en-US', 'resetPasswordRequirements') }))] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "confirmPassword", className: "block text-sm font-medium mb-1", children: getMessage('en-US', 'resetPasswordConfirmLabel') }), _jsx("input", { id: "confirmPassword", type: "password", required: true, value: confirmPassword, onChange: (e) => setConfirmPassword(e.target.value), onBlur: () => setTouchedConfirm(true), className: "w-full h-11 px-3 border border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", "aria-invalid": touchedConfirm && mismatch }), touchedConfirm && mismatch && (_jsx("p", { className: "mt-1 text-xs text-red-600", children: getMessage('en-US', 'resetPasswordMismatch') }))] }), error && (_jsx("div", { className: "bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded", role: "alert", children: error })), _jsx(Button, { htmlType: "submit", disabled: loading || !hasToken, "aria-busy": loading, className: "w-full bg-primary text-white py-2 rounded-md hover:bg-primary/80 disabled:opacity-60", children: loading ? (_jsxs("span", { className: "flex items-center justify-center gap-2", children: [_jsx("span", { className: "h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent", "aria-hidden": "true" }), getMessage('en-US', 'resetPasswordSubmit')] })) : (getMessage('en-US', 'resetPasswordSubmit')) }), _jsxs("div", { className: "text-center text-sm text-gray-700", children: [_jsx(Link, { to: loginHref, className: "text-primary hover:underline", children: getMessage('en-US', 'goToLogin') }), _jsx("span", { className: "mx-1", "aria-hidden": "true", children: "\u00B7" }), _jsx(Link, { to: forgotHref, className: "text-primary hover:underline", children: getMessage('en-US', 'forgotPassword') })] })] }))] }) }) }));
};
//# sourceMappingURL=ResetPasswordPage.js.map